import { createGlobalStyle } from 'styled-components';
import { colors, typography, spacing } from './theme';

export const GlobalStyles = createGlobalStyle`
  /* Reset and normalize */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* Root styles */
  html {
    font-size: 16px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    scroll-behavior: smooth;
  }

  body {
    font-family: ${typography.fontFamilies.primary};
    font-size: ${typography.fontSizes.base};
    font-weight: ${typography.fontWeights.normal};
    line-height: ${typography.lineHeights.normal};
    color: ${colors.text.primary};
    background-color: ${colors.background.primary};
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${typography.fontWeights.semibold};
    line-height: ${typography.lineHeights.tight};
    color: ${colors.text.primary};
    margin-bottom: ${spacing[4]};
  }

  h1 {
    font-size: ${typography.fontSizes['4xl']};
    font-weight: ${typography.fontWeights.bold};
    
    @media (max-width: 768px) {
      font-size: ${typography.fontSizes['3xl']};
    }
  }

  h2 {
    font-size: ${typography.fontSizes['3xl']};
    
    @media (max-width: 768px) {
      font-size: ${typography.fontSizes['2xl']};
    }
  }

  h3 {
    font-size: ${typography.fontSizes['2xl']};
    
    @media (max-width: 768px) {
      font-size: ${typography.fontSizes.xl};
    }
  }

  h4 {
    font-size: ${typography.fontSizes.xl};
  }

  h5 {
    font-size: ${typography.fontSizes.lg};
  }

  h6 {
    font-size: ${typography.fontSizes.base};
  }

  p {
    margin-bottom: ${spacing[4]};
    color: ${colors.text.secondary};
    line-height: ${typography.lineHeights.relaxed};
  }

  /* Links */
  a {
    color: ${colors.text.link};
    text-decoration: none;
    transition: color 0.15s ease-in-out;

    &:hover {
      color: ${colors.text.linkHover};
      text-decoration: underline;
    }

    &:focus {
      outline: 2px solid ${colors.primary[500]};
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  /* Lists */
  ul, ol {
    margin-bottom: ${spacing[4]};
    padding-left: ${spacing[6]};
  }

  li {
    margin-bottom: ${spacing[2]};
    color: ${colors.text.secondary};
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Form elements */
  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:focus {
      outline: 2px solid ${colors.primary[500]};
      outline-offset: 2px;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    background-color: ${colors.white};
    border: 1px solid ${colors.border.normal};
    border-radius: 6px;
    
    &:focus {
      outline: 2px solid ${colors.primary[500]};
      outline-offset: 2px;
      border-color: ${colors.primary[500]};
    }

    &::placeholder {
      color: ${colors.text.tertiary};
    }

    &:disabled {
      background-color: ${colors.background.tertiary};
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  /* Utility classes */
  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 ${spacing[4]};

    @media (min-width: 640px) {
      padding: 0 ${spacing[6]};
    }

    @media (min-width: 1024px) {
      padding: 0 ${spacing[8]};
    }
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .flex {
    display: flex;
  }

  .flex-col {
    flex-direction: column;
  }

  .items-center {
    align-items: center;
  }

  .justify-center {
    justify-content: center;
  }

  .justify-between {
    justify-content: space-between;
  }

  .gap-4 {
    gap: ${spacing[4]};
  }

  .gap-6 {
    gap: ${spacing[6]};
  }

  .mb-4 {
    margin-bottom: ${spacing[4]};
  }

  .mb-6 {
    margin-bottom: ${spacing[6]};
  }

  .mb-8 {
    margin-bottom: ${spacing[8]};
  }

  .mt-4 {
    margin-top: ${spacing[4]};
  }

  .mt-6 {
    margin-top: ${spacing[6]};
  }

  .mt-8 {
    margin-top: ${spacing[8]};
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.background.secondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.gray[400]};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.gray[500]};
  }

  /* Loading animations */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.3s ease-out;
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  /* Focus management for accessibility */
  .focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }

  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }

    a, a:visited {
      text-decoration: underline;
    }

    a[href]:after {
      content: " (" attr(href) ")";
    }

    .no-print {
      display: none !important;
    }
  }
`;
